import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListStockMovementsInput } from "@nexxt/contracts/logistics";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the movements of stock (50-api/logistics.md §7, §8): one factory, the same keys for loaders,
// screens and mutations. The sync of a movement keeps its key under `of`, so whatever reloads the movements reloads it
// too.

const movements = api.logistics["stock-movements"];

type ListPage = InferResponseType<typeof movements.$get, 200>;
export type StockMovement = ListPage["items"][number];
export type StockMovementDetail = InferResponseType<(typeof movements)[":movementId"]["$get"], 200>;
export type StockMovementLine = StockMovementDetail["lines"][number];
export type MovementExecution = StockMovementDetail["executions"][number];

/** The filters of the list input (`listStockMovementsInput`), without the paging the queries add. */
export type StockMovementFilters = Pick<ListStockMovementsInput, "status" | "type" | "location" | "q">;

/** The tabs of the page of a movement, in the order it shows them. */
export const stockMovementTabs = ["lines", "executions", "systems", "details"] as const;
export type StockMovementTab = (typeof stockMovementTabs)[number];

const PAGE_SIZE = 50;

const keys = {
  all: ["stock-movements"] as const,
  /** Prefix of every cached list: a write puts the movement it gets back in each of them. */
  lists: ["stock-movements", "list"] as const,
  /** Prefix of what is cached of one movement besides its detail: its sync. */
  of: (movementId: string) => ["stock-movements", "of", movementId] as const,
};

export const stockMovementQueries = {
  ...keys,

  list: (filters: StockMovementFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          movements.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** With its lines, the units that left and arrived, and the executions of the warehouse. */
  detail: (movementId: string) =>
    queryOptions({
      queryKey: [...keys.all, "detail", movementId],
      queryFn: ({ signal }) => unwrap(movements[":movementId"].$get({ param: { movementId } }, { init: { signal } })),
    }),

  /** How the warehouse (I-10) and Business Central (I-25) took its last state: one state per message. */
  sync: (movementId: string) =>
    queryOptions({
      queryKey: [...keys.of(movementId), "sync"],
      queryFn: ({ signal }) =>
        unwrap(movements[":movementId"].sync.$get({ param: { movementId } }, { init: { signal } })),
    }),
};
